import { useCallback, useContext } from "react";

import { AnalyticsContext, DELIVERY_SECTION_ELEMENT } from "@swiggy-private/checkout-core";
import { scrollElementIntoView } from "@swiggy-private/core-ui";
import { PLBillCtaAction, PlanDetails } from "@swiggy-private/cart-types";

import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { DeliverySectionContext } from "@cart-client/contexts/DeliverySectionContext";
import { CART_ELEMENT_IDS } from "@cart-client/components/cart-renderer/constants";
import { clickBillDetailAddTip } from "@cart-client/analytics/events/BillDetails";
import { getDefaultTipDetails } from "@cart-client/helpers/DeTip";
import { renderHalfcardOnMakeItFreeClick } from "@cart-client/helpers/BillDetailsHelper";
import { UpdateCartData } from "@cart-client/interfaces/Checkout";

interface UsePLBillActionsOptions {
    setBillDetailsOneCardComms?: VoidFunction;
    planDetails?: PlanDetails;
}

const usePLBillActions = ({ setBillDetailsOneCardComms, planDetails }: UsePLBillActionsOptions = {}): Record<
    PLBillCtaAction,
    VoidFunction
> => {
    const { cartState } = useContext(CartContext);
    const { doUpdateCart, updateCartInProgress, setShowM2ChalfcardOnClick } = useContext(UpdateCartContext);
    const { clickEvent } = useContext(AnalyticsContext);
    const { setDeliveryTab } = useContext(DeliverySectionContext);

    const handleAddTip = useCallback(async () => {
        const { reviewedCart, localCart } = cartState;
        const defaultTipDetails = getDefaultTipDetails(reviewedCart?.data);

        scrollElementIntoView(CART_ELEMENT_IDS.DE_TIP_CONTAINER, false);
        setDeliveryTab(DELIVERY_SECTION_ELEMENT.TIP);
        clickBillDetailAddTip(clickEvent);

        if (!updateCartInProgress) {
            await doUpdateCart({
                ...(localCart as UpdateCartData),
                tipDetails: {
                    amount: defaultTipDetails.amount,
                    optIn: defaultTipDetails.optIn,
                },
            });
        }
    }, [cartState, doUpdateCart, updateCartInProgress, clickEvent, setDeliveryTab]);

    const handleMakeItFree = useCallback(() => {
        renderHalfcardOnMakeItFreeClick({
            setShowM2ChalfcardOnClick,
            setBillDetailsOneCardComms,
            clickEvent,
            planDetails,
        });
    }, [setShowM2ChalfcardOnClick, setBillDetailsOneCardComms, clickEvent, planDetails]);

    return {
        [PLBillCtaAction.ADD_TIP]: handleAddTip,
        [PLBillCtaAction.MAKE_IT_FREE]: handleMakeItFree,
    };
};

export default usePLBillActions;
